import { SaveOutlined } from '@ant-design/icons';
import { Button, InputNumber, message } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../contexts/ApiContext';
import {
    referenceDisplayFactor,
    toCanonicalReference,
    toDisplayReference,
    type StatisticsCurrency,
} from '../../domain/statisticsReferenceCurrency';
import { extractSeriesPoints } from '../../domain/trafficLight';
import TrafficLight from './TrafficLight';

interface AverageData {
    series: string;
    value: number;
}

interface ChartWrapperProps {
    children: React.ReactElement;
    chartSeries: string; // The series name for the averages table
    defaultReferenceValue?: number;
    // Displayed currency and rate for money series. The reference is persisted in EUR
    // (canonical) and scaled onto the displayed currency so the line always stays on the
    // data's scale when the currency toggles. Non-money (count/grams) series omit these.
    currency?: StatisticsCurrency;
    exchangeRate?: number;
    convertCurrency?: boolean;
}

const ChartWrapper: React.FC<ChartWrapperProps> = ({
    children,
    chartSeries,
    defaultReferenceValue = 0,
    currency,
    exchangeRate,
    convertCurrency = false,
}) => {
    const { t } = useTranslation();
    const { statistics } = useApi();
    // Canonical (EUR) reference persisted in the averages table.
    const [canonicalValue, setCanonicalValue] = useState<number>(defaultReferenceValue);
    // Value shown in the input box, expressed in the currently displayed currency.
    const [inputValue, setInputValue] = useState<number>(defaultReferenceValue);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const factor = referenceDisplayFactor(currency, exchangeRate, convertCurrency);
    // Reference line value on the chart, always on the displayed currency's scale.
    const referenceValue = useMemo(
        () => toDisplayReference(canonicalValue, factor),
        [canonicalValue, factor],
    );

    // Keep the editable input in sync with the displayed currency whenever the saved
    // reference or the conversion factor (currency toggle) changes.
    useEffect(() => {
        setInputValue(toDisplayReference(canonicalValue, factor));
    }, [canonicalValue, factor]);

    // Load reference value from averages table on mount
    useEffect(() => {
        const loadReferenceValue = async () => {
            try {
                setLoading(true);
                const response = await statistics.getAverages();
                if (response.success && response.data?.data) {
                    const seriesData = response.data.data.find(
                        (item: AverageData) => item.series === chartSeries,
                    );
                    if (seriesData && seriesData.value !== null) {
                        setCanonicalValue(seriesData.value);
                    }
                }
            } catch (error) {
                console.error('Failed to load reference value:', error);
            } finally {
                setLoading(false);
            }
        };

        loadReferenceValue();
    }, [chartSeries, statistics]);

    // Save reference value to averages table (persisted canonically in EUR)
    const saveReferenceValue = async () => {
        try {
            setSaving(true);
            const canonical = toCanonicalReference(inputValue, factor);
            const response = await statistics.updateAverage(chartSeries, canonical);
            if (response.success) {
                setCanonicalValue(canonical);
                message.success(
                    t('statistics.messages.referenceSaved', 'Reference value saved successfully'),
                );
            } else {
                message.error(
                    t('statistics.messages.referenceSaveError', 'Failed to save reference value'),
                );
            }
        } catch (error) {
            console.error('Failed to save reference value:', error);
            message.error(
                t('statistics.messages.referenceSaveError', 'Failed to save reference value'),
            );
        } finally {
            setSaving(false);
        }
    };

    // Clone the child chart component and add reference line to its options
    const enhancedChild = React.cloneElement(children, {
        ...children.props,
        referenceValue,
    });

    // Normalise the wrapped chart's data into rateable points for the traffic light.
    // The points share the reference's displayed-currency scale, so the ratio matches
    // what the reference line shows on the chart.
    const childData = (children.props as { data?: unknown }).data;
    const points = useMemo(() => extractSeriesPoints(childData), [childData]);

    return (
        <div style={{ position: 'relative' }}>
            {/* Reference value controls in top-right corner */}
            <div
                style={{
                    position: 'absolute',
                    /* lift further into the Card header area so controls align with the Card title */
                    top: -48,
                    right: 12,
                    zIndex: 1000,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #d9d9d9',
                    borderRadius: 6,
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: '12px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                }}
            >
                <span style={{ color: '#666', fontSize: '11px' }}>
                    {t('statistics.charts.reference', 'Ref:')}
                </span>
                <InputNumber
                    size="small"
                    value={inputValue}
                    onChange={value => setInputValue(value ?? 0)}
                    style={{ width: 80 }}
                    precision={0}
                    min={0}
                    disabled={loading}
                />
                <Button
                    type="primary"
                    size="small"
                    icon={<SaveOutlined />}
                    onClick={saveReferenceValue}
                    loading={saving}
                    disabled={loading}
                    style={{ padding: '0 8px', height: '24px' }}
                />
            </div>

            {/* Chart component with reference line */}
            {enhancedChild}

            {/* Per-chart target-vs-actual traffic light (only shows once a target is set) */}
            <TrafficLight points={points} referenceValue={referenceValue} />
        </div>
    );
};

export default ChartWrapper;
